import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { Endgood } from './endgoodApi';
import { EndgoodCategory } from './endgoodCategoryApi';
import { HsnCode } from './hsnApi';
import { ProcessFlow } from './processFlowApi';

export interface BomHeader {
  id: number;
  bomNumber: string;
  bomName: string;
  version: string;
  status: string;
  effectiveDate: string;
  expiryDate: string;
  description: string;
  totalCost: number;
  laborCost: number;
  overheadCost: number;
  materialCost: number;
  productionQuantity: number;
  unitCost: number;
  approvedBy: string;
  approvedDate: string;
  isActive: boolean;
  branchId: number;
  createdBy: number;
  updatedBy: number;
  createdAt?: string;
  updatedAt?: string;
  endgood?: Endgood & {
    hsn?: HsnCode;
    endgoodCategory?: EndgoodCategory;
  };
  processFlow?: ProcessFlow;
  // Flattened fields from list API (when endgood nested object not present)
  endgoodId?: number;
  endgoodName?: string;
  endgoodCategoryId?: number;
  endgoodCategoryName?: string;
  processFlowId?: number;
  endgoodProcessWeightAvailable?: boolean;
}

export interface BomHeaderResponse extends PaginatedResponse<BomHeader> {}

export interface BomHeaderCreateRequest {
  bomNumber: string;
  bomName: string;
  version: string;
  status: string;
  effectiveDate: string;
  expiryDate: string;
  description: string;
  totalCost: number;
  laborCost: number;
  overheadCost: number;
  materialCost: number;
  productionQuantity: number;
  unitCost: number;
  approvedBy: string;
  approvedDate: string;
  isActive: boolean;
  branchId: number;
  processFlowId?: number; // Optional field to link to Process Flow
  createdBy: number;
  updatedBy: number;
  endgoodId: number;
}

export interface BomHeaderUpdateRequest extends BomHeaderCreateRequest {
  id: number;
}

class BomHeaderApiService extends BaseApiService {
  async getBomHeaders(): Promise<BomHeader[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomHeader[]>>(
        API_ENDPOINTS.BOM_HEADERS.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch BOM headers');
    } catch (error) {
      // console.error('Error fetching BOM headers:', error);
      toast.error('Failed to fetch BOM headers');
      throw error;
    }
  }

  async getBomHeadersPaginated(
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    statusFilter?: string,
    dateFilter?: string,
    missingProcessWeightOnly?: boolean
  ): Promise<BomHeaderResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (searchTerm) params.append('searchTerm', searchTerm);
      if (statusFilter) params.append('statusFilter', statusFilter);
      if (dateFilter) params.append('dateFilter', dateFilter);
      if (missingProcessWeightOnly === true) params.append('endgoodProcessWeightAvailable', 'false');

      const endpoint = `${API_ENDPOINTS.BOM_HEADERS.LIST}?${params.toString()}`;
      const response = await this.makeRequest<BomHeaderResponse>(
        endpoint,
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('Error fetching paginated BOM headers:', error);
      toast.error('Failed to fetch BOM headers');
      throw error;
    }
  }

  async searchBomHeadersPaginated(
    searchTerm: string,
    page: number = 1,
    size: number = 20
  ): Promise<BomHeaderResponse> {
    try {
      const params = new URLSearchParams();
      params.append('q', searchTerm.trim());
      params.append('page', page.toString());
      params.append('size', size.toString());

      const endpoint = `${API_ENDPOINTS.BOM_HEADERS.SEARCH}?${params.toString()}`;
      const response = await this.makeRequest<BomHeaderResponse>(
        endpoint,
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('Error searching BOM headers:', error);
      toast.error('Failed to search BOM headers');
      throw error;
    }
  }

  async getBomHeaderById(id: string): Promise<BomHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<BomHeader>>(
        API_ENDPOINTS.BOM_HEADERS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch BOM header');
      }
    } catch (error) {
      // console.error('Error fetching BOM header:', error);
      toast.error('Failed to fetch BOM header details');
      throw error;
    }
  }

  async createBomHeader(bomHeaderData: BomHeaderCreateRequest): Promise<BomHeader> {
    try {
      // console.log('API Service - Creating BOM header with data:', bomHeaderData);
      // console.log('API Service - endgoodId value:', bomHeaderData.endgoodId);
      // console.log('API Service - endgoodId type:', typeof bomHeaderData.endgoodId);
      
      const response = await this.makeRequest<ApiResponse<BomHeader>>(
        API_ENDPOINTS.BOM_HEADERS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(bomHeaderData)
        }
      );
      
      if (response.success) {
        // console.log('API Service - BOM header created successfully:', response.data);
        toast.success('BOM header created successfully');
        return response.data;
      } else {
        // console.error('API Service - Failed to create BOM header:', response.message);
        throw new Error(response.message || 'Failed to create BOM header');
      }
    } catch (error) {
      // console.error('Error creating BOM header:', error);
      // console.error('Error details:', error);
      toast.error('Failed to create BOM header');
      throw error;
    }
  }

  async updateBomHeader(id: string, bomHeaderData: BomHeaderUpdateRequest): Promise<BomHeader> {
    try {
      const response = await this.makeRequest<ApiResponse<BomHeader>>(
        API_ENDPOINTS.BOM_HEADERS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(bomHeaderData)
        }
      );
      
      if (response.success) {
        toast.success('BOM header updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update BOM header');
      }
    } catch (error) {
      // console.error('Error updating BOM header:', error);
      toast.error('Failed to update BOM header');
      throw error;
    }
  }

  async deleteBomHeader(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.BOM_HEADERS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('BOM header deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete BOM header');
      }
    } catch (error) {
      // console.error('Error deleting BOM header:', error);
      toast.error('Failed to delete BOM header');
      throw error;
    }
  }


  async getBomHeadersByEndgood(endgoodId: number): Promise<BomHeader[]> {
    try {
      const response = await this.makeRequest<ApiResponse<BomHeader[]>>(
        API_ENDPOINTS.BOM_HEADERS.BY_ENDGOOD(endgoodId.toString()),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch BOM headers for endgood');
      }
    } catch (error) {
      // console.error('Error fetching BOM headers for endgood:', error);
      toast.error('Failed to fetch BOM headers for endgood');
      throw error;
    }
  }

}

export const bomHeaderApiService = new BomHeaderApiService();
